import { supabase } from "@/lib/supabase";
import { ProfileCreationService } from "./profile-creation.service";
import { verificationService } from "./verification.service";
import { UserProfile, Address } from "@/types/user";

/**
 * Profile Validation Utilities
 * Single Responsibility: Only handles validation logic
 */
const ProfileValidation = {
  /**
   * Validate phone number (Moroccan format)
   */
  isValidPhone(phone: string): boolean {
    if (!phone || phone.trim() === "") return true; // Empty is allowed
    const phoneRegex = /^(?:\+212|0)[5-7]\d{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  },

  /**
   * Validate date string
   */
  isValidDate(date: string): boolean {
    if (!date || date.trim() === "") return true; // Empty is allowed
    const dateObj = new Date(date);
    return dateObj instanceof Date && !isNaN(dateObj.getTime());
  },

  /**
   * Validate name (min 2 characters)
   */
  isValidName(name: string): boolean {
    if (!name || name.trim() === "") return true; // Empty is allowed
    return name.trim().length >= 2;
  },

  /**
   * Validate profile update data
   * @throws Error if validation fails
   */
  validateProfileUpdate(updates: Partial<UserProfile>): void {
    if (updates.phone !== undefined && !this.isValidPhone(updates.phone)) {
      throw new Error("Numéro de téléphone invalide. Format attendu: 06XXXXXXXX ou +212XXXXXXXX");
    }

    if (updates.birthdate !== undefined && !this.isValidDate(updates.birthdate)) {
      throw new Error("Date de naissance invalide");
    }

    if (updates.first_name !== undefined && !this.isValidName(updates.first_name)) {
      throw new Error("Le prénom doit contenir au moins 2 caractères");
    }

    if (updates.last_name !== undefined && !this.isValidName(updates.last_name)) {
      throw new Error("Le nom doit contenir au moins 2 caractères");
    }
  },
};

/**
 * Profile Data Mapper
 * Single Responsibility: Only handles data transformation
 */
const ProfileMapper = {
  /**
   * Parse address from database (can be JSON string or object)
   */
  parseAddress(address: unknown): Address | undefined {
    if (!address) return undefined;
    if (typeof address === "string") {
      try {
        return JSON.parse(address);
      } catch {
        return undefined;
      }
    }
    return address as Address;
  },

  /**
   * Parse notification preferences
   */
  parseNotificationPreferences(prefs: unknown): { email: boolean; push: boolean } {
    const defaults = { email: true, push: true };
    if (!prefs) return defaults;
    if (typeof prefs === "string") {
      try {
        const parsed = JSON.parse(prefs);
        return {
          email: parsed.email ?? true,
          push: parsed.push ?? true,
        };
      } catch {
        return defaults;
      }
    }
    const objPrefs = prefs as Record<string, unknown>;
    return {
      email: (objPrefs.email as boolean) ?? true,
      push: (objPrefs.push as boolean) ?? true,
    };
  },

  /**
   * Map database row to UserProfile type
   */
  toUserProfile(data: Record<string, unknown>): UserProfile {
    return {
      id: data.id as string,
      email: (data.email as string) || "",
      first_name: (data.first_name as string) || "",
      last_name: (data.last_name as string) || "",
      phone: (data.phone as string) || "",
      role: (data.role as UserProfile["role"]) || "renter",
      verified_tenant: (data.verified_tenant as boolean) || false,
      verified_host: (data.verified_host as boolean) || false,
      avatar_url: data.avatar_url as string | undefined,
      agency_name: data.agency_name as string | undefined,
      rating: data.rating as number | undefined,
      birthdate: data.birthdate as string | undefined,
      address: this.parseAddress(data.address),
      notification_preferences: this.parseNotificationPreferences(data.notification_preferences),
      email_verified: (data.email_verified as boolean) || false,
      phone_verified: (data.phone_verified as boolean) || false,
      created_at: data.created_at as string | undefined,
      updated_at: data.updated_at as string | undefined,
    };
  },

  /**
   * Prepare update data for database
   */
  prepareUpdateData(updates: Partial<UserProfile>): Record<string, unknown> {
    const dbData: Record<string, unknown> = {};

    if (updates.first_name !== undefined) dbData.first_name = updates.first_name.trim();
    if (updates.last_name !== undefined) dbData.last_name = updates.last_name.trim();
    if (updates.phone !== undefined) dbData.phone = updates.phone.trim();
    if (updates.birthdate !== undefined) dbData.birthdate = updates.birthdate;
    if (updates.address !== undefined) {
      dbData.address =
        typeof updates.address === "string" ? updates.address : JSON.stringify(updates.address);
    }
    if (updates.notification_preferences !== undefined) {
      dbData.notification_preferences = updates.notification_preferences;
    }

    return dbData;
  },
};

/**
 * Profile Service
 * Single Responsibility: Profile CRUD operations
 * Open/Closed: Extensible via composition (validation, mapping)
 * Dependency Inversion: Depends on Supabase client abstraction
 */
export const profileService = {
  /**
   * Get current user profile
   */
  async getMyProfile(): Promise<UserProfile> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await supabase.from("profiles").select("*").eq("id", user.id).single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Profile not found");

    return ProfileMapper.toUserProfile(data as Record<string, unknown>);
  },

  /**
   * Get profile by ID (requires RLS to allow read - may fail for anon viewing other users)
   */
  async getProfileById(id: string): Promise<UserProfile> {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", id).single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error("Profile not found");

    return ProfileMapper.toUserProfile(data as Record<string, unknown>);
  },

  /**
   * Get public profile by ID via RPC (works for anon - for agency/owner pages).
   * Returns null if profile not found or not a vehicle owner.
   */
  async getPublicProfileById(id: string): Promise<UserProfile | null> {
    const { data: json, error } = await supabase.rpc("get_public_profile_for_vehicle_owner", {
      p_profile_id: id,
    });

    if (error || json == null) return null;

    const row = json as Record<string, unknown>;
    return ProfileMapper.toUserProfile({
      id: row.id,
      email: "",
      first_name: row.first_name ?? "",
      last_name: row.last_name ?? "",
      phone: "",
      role: row.role ?? "owner",
      verified_tenant: row.verified_tenant ?? false,
      verified_host: row.verified_host ?? false,
      avatar_url: row.avatar_url,
      agency_name: row.agency_name,
      rating: undefined,
      created_at: row.created_at,
      updated_at: undefined,
    });
  },

  /**
   * Update profile with validation
   */
  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Validate updates
    ProfileValidation.validateProfileUpdate(updates);

    // Prepare data for database
    const dbData = ProfileMapper.prepareUpdateData(updates);

    // Skip update if no data to update
    if (Object.keys(dbData).length === 0) {
      return this.getMyProfile();
    }

    const { data: updated, error } = await supabase
      .from("profiles")
      .update(dbData)
      .eq("id", user.id)
      .select()
      .single();

    if (error) throw new Error(error.message);

    // Sync with auth metadata if name changed
    if (updates.first_name || updates.last_name) {
      await this.syncAuthMetadata(updates.first_name, updates.last_name);
    }

    return ProfileMapper.toUserProfile(updated as Record<string, unknown>);
  },

  /**
   * Upload avatar
   */
  async uploadAvatar(file: File): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { uploadToStorage, ALLOWED_IMAGE_TYPES } = await import("@/lib/utils");

    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const uploadResult = await uploadToStorage(file, {
      bucket: "avatars",
      path: fileName,
      allowedTypes: [...ALLOWED_IMAGE_TYPES] as string[],
      maxSize: 5 * 1024 * 1024,
      upsert: false,
    });

    const publicUrl =
      uploadResult.publicUrl ||
      supabase.storage.from("avatars").getPublicUrl(uploadResult.path).data.publicUrl;

    const { error: updateError } = await supabase
      .from("profiles")
      .update({ avatar_url: publicUrl })
      .eq("id", user.id);

    if (updateError) throw new Error(updateError.message);
    return publicUrl;
  },

  /**
   * Sync auth metadata with profile
   */
  async syncAuthMetadata(firstName?: string, lastName?: string): Promise<void> {
    const metadata: Record<string, string> = {};
    if (firstName) metadata.first_name = firstName;
    if (lastName) metadata.last_name = lastName;

    if (Object.keys(metadata).length > 0) {
      await supabase.auth.updateUser({ data: metadata });
    }
  },

  /**
   * Get user role
   * Delegate to ProfileCreationService for robustness
   */
  async getUserRole(userId: string): Promise<"owner" | "renter" | "admin" | null> {
    return await ProfileCreationService.getUserRole(userId);
  },

  /**
   * Check if user is verified tenant
   * Uses verified_tenant field and verification requests
   */
  async isVerifiedTenant(userId: string): Promise<boolean> {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("verified_tenant")
      .eq("id", userId)
      .single();

    if (error || !profile) return false;

    // Check if profile is verified
    if (profile.verified_tenant === true) return true;

    // Check verification requests as fallback
    try {
      const verificationStatus = await verificationService.checkExistingVerificationRequest(
        userId,
        "tenant"
      );

      if (verificationStatus.exists && verificationStatus.status === "approved") {
        // Sync profile verification status
        await supabase.from("profiles").update({ verified_tenant: true }).eq("id", userId);
        return true;
      }
    } catch {
      // Ignore errors, return false
    }

    return false;
  },

  /**
   * Check if user is verified host/owner
   * Uses verified_host field and verification requests
   */
  async isVerifiedHost(userId: string): Promise<boolean> {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("verified_host")
      .eq("id", userId)
      .single();

    if (error || !profile) return false;

    // Check if profile is verified
    if (profile.verified_host === true) return true;

    // Check verification requests as fallback
    try {
      const verificationStatus = await verificationService.checkExistingVerificationRequest(
        userId,
        "host"
      );

      if (verificationStatus.exists && verificationStatus.status === "approved") {
        // Sync profile verification status
        await supabase.from("profiles").update({ verified_host: true }).eq("id", userId);
        return true;
      }
    } catch {
      // Ignore errors, return false
    }

    return false;
  },

  /**
   * Ensure user profile exists
   * Delegate to ProfileCreationService
   */
  async ensureUserProfile(userId: string): Promise<boolean> {
    return await ProfileCreationService.ensureProfileExists(userId);
  },

  /**
   * Calculate profile completion percentage
   */
  calculateProfileCompletion(profile: Partial<UserProfile>): number {
    const requiredFields = [
      "first_name",
      "last_name",
      "phone",
      "birthdate",
      "avatar_url",
      "address",
      "email_verified",
      "phone_verified",
    ];

    const completedFields = requiredFields.filter((field) => {
      if (field === "address") {
        const addr = profile.address;
        return addr && typeof addr === "object" && "street" in addr && addr.street;
      }
      if (field === "email_verified") return profile.email_verified;
      if (field === "phone_verified") return profile.phone_verified;
      return profile[field as keyof UserProfile];
    });

    return Math.round((completedFields.length / requiredFields.length) * 100);
  },
};

// Export validation and mapper for testing
export { ProfileValidation, ProfileMapper };
